<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceFooter',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    currency: {
      type: Object,
      required: true,
    },
    exchangedCurrency: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.currency,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('currency');
    }
  );
  watch(
    () => props.exchangedCurrency,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('exchangedCurrency');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <table class="ms-auto" v-if="invoice.totals">
    <tbody>
      <tr>
        <td class="pe-4">
          {{ pgettext('Invoice', 'Taxable subtotal') }}
        </td>
        <td class="text-end">
          <span>
            {{
              money(invoice.totals.subtotal, invoice.currency, currency.digits)
            }}
          </span>
        </td>
        <td
          class="text-end ps-4"
          v-if="invoice.exchanged_totals && exchangedCurrency"
        >
          <ItpText small>
            {{
              money(
                invoice.exchanged_totals.subtotal,
                invoice.exchanged_currency,
                exchangedCurrency.digits
              )
            }}
          </ItpText>
        </td>
      </tr>
      <tr v-for="(tax, index) in invoice.totals.taxes">
        <td class="pe-4">
          <ItpText :text="gettext('Tax')" small muted> </ItpText>
          <ItpText :text="tax.tax_code" small muted> </ItpText>
        </td>
        <td class="text-end">
          <span>
            {{ money(tax.amount, invoice.currency, currency.digits) }}
          </span>
        </td>
        <td
          class="text-end ps-4"
          v-if="invoice.exchanged_totals && exchangedCurrency"
        >
          <ItpText small>
            {{
              money(
                invoice.exchanged_totals.taxes[index].amount,
                invoice.exchanged_currency,
                exchangedCurrency.digits
              )
            }}
          </ItpText>
        </td>
      </tr>
      <tr>
        <td class="pe-4">
          <ItpText :text="gettext('Total')" strong> </ItpText>
        </td>
        <td class="text-end">
          <strong>
            {{ money(invoice.totals.total, invoice.currency, currency.digits) }}
          </strong>
        </td>
        <td
          class="text-end ps-4"
          v-if="invoice.exchanged_totals && exchangedCurrency"
        >
          <ItpText small>
            {{
              money(
                invoice.exchanged_totals.total,
                invoice.exchanged_currency,
                exchangedCurrency.digits
              )
            }}
          </ItpText>
        </td>
      </tr>
    </tbody>
  </table>
</template>
